import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { usePagination } from '@/hooks/usePagination';
import { useDebounce } from '@/hooks/useDebounce';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import { TenantFilterSidebar, TenantFilterValues } from '@/components/features/tenants';
import { Building2, Plus, Filter, Eye, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Tenant } from '@/types/tenant.types';
import { useAuth } from '@/hooks/useAuth';
import { AddTenantModal } from '@/components/features/tenants/AddTenantModal';
import { useI18n } from '@/contexts/I18nContext';

const initialFilters: TenantFilterValues = {
  search: '',
  status: '',
  subscription_type: '',
  integration_type: '',
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'suspended': return 'bg-destructive/10 text-destructive border-destructive/20';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

const getSubscriptionColor = (type: string) => {
  switch (type) {
    case 'enterprise': return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    case 'premium': return 'bg-primary/10 text-primary border-primary/20';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

export default function TenantsList() {
  const navigate = useNavigate();
  const { tp } = useI18n();
  const { get, loading } = useApi();
  const { user } = useAuth();
  const pagination = usePagination();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filters, setFilters] = useState<TenantFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const debouncedSearch = useDebounce(filters.search, 300);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return tp.commonExt.active;
      case 'suspended': return tp.commonExt.suspended;
      default: return tp.commonExt.inactive;
    }
  };

  const getSubscriptionLabel = (type: string) => {
    switch (type) {
      case 'enterprise': return tp.tenants.enterpriseSubscription;
      case 'premium': return tp.tenants.premiumSubscription;
      default: return tp.tenants.basicSubscription;
    }
  };

  const getIntegrationLabel = (type: string) => {
    switch (type) {
      case 'api': return tp.tenants.apiIntegration;
      case 'webhook': return tp.tenants.webhookIntegration;
      default: return tp.tenants.manualIntegration;
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [pagination.page, debouncedSearch, filters.status, filters.subscription_type, filters.integration_type]);

  const fetchTenants = async () => {
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.status) params.status = filters.status;
      if (filters.subscription_type) params.subscription_type = filters.subscription_type;
      if (filters.integration_type) params.integration_type = filters.integration_type;

      const response: any = await get('/api/v1/dashboard/tenants', params);
      const list = response?.list || response?.data?.list || [];
      setTenants(list);
      const meta = response?.meta || response?.data?.meta || {};
      pagination.updatePagination({ total_pages: meta.totalPages || 0, total: meta.total || 0 });
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const handleClearFilters = () => { setFilters(initialFilters); pagination.reset(); };
  const handleFilterChange = (key: keyof TenantFilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };
  const handleTenantClick = (tenant: Tenant) => navigate(`/dashboard/tenants/${tenant.id}`);

  const columns = [
    { header: tp.commonExt.name, accessor: 'name' as keyof Tenant, className: 'font-medium' },
    { header: tp.commonExt.email, accessor: 'email' as keyof Tenant },
    { header: tp.commonExt.phone, accessor: 'phone' as keyof Tenant },
    {
      header: tp.commonExt.status,
      accessor: (row: Tenant) => (
        <Badge variant="outline" className={getStatusColor(row.status)}>{getStatusLabel(row.status)}</Badge>
      ),
    },
    {
      header: tp.tenants.subscription,
      accessor: (row: Tenant) => (
        <Badge variant="outline" className={getSubscriptionColor(row.subscription_type)}>{getSubscriptionLabel(row.subscription_type)}</Badge>
      ),
    },
    {
      header: tp.tenants.integration,
      accessor: (row: Tenant) => getIntegrationLabel(row.integration_type),
      className: 'text-muted-foreground',
    },
    { header: tp.tenants.shipments, accessor: (row: Tenant) => row.shipments_stats.total, className: 'text-center' },
  ];

  const renderActions = (row: Tenant) => (
    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleTenantClick(row); }} title={tp.commonExt.viewDetails}>
      <Eye className="h-4 w-4" />
    </Button>
  );

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  const FilterSidebarContent = (
    <TenantFilterSidebar filters={filters} onFilterChange={handleFilterChange} onClearFilters={handleClearFilters} />
  );

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ChevronRight className="h-5 w-5" /></Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">{tp.tenants.listTitle}</h1>
              <p className="text-sm text-muted-foreground">{tp.tenants.listSubtitle}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button onClick={() => setShowAddModal(true)}><Plus className="h-4 w-4 ms-2" />{tp.tenants.addTenant}</Button>
            )}
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild><Button variant="outline" size="icon" className="lg:hidden"><Filter className="h-4 w-4" /></Button></SheetTrigger>
              <SheetContent side="right" className="w-72 p-4"><div className="mt-6">{FilterSidebarContent}</div></SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <div className="flex gap-4 h-[calc(100vh-180px)]">
        <div className="hidden lg:block h-full overflow-y-auto">{FilterSidebarContent}</div>
        <div className="flex-1 min-w-0">
          {loading ? (
            <LoadingSkeleton type="table" rows={10} />
          ) : tenants.length === 0 ? (
            <EmptyState icon={<Building2 className="h-16 w-16" />} title={tp.tenants.noTenants} description={tp.tenants.noTenantsDescription}
              action={hasActiveFilters ? { label: tp.commonExt.clearFilters, onClick: handleClearFilters } : undefined}
            />
          ) : (
            <DataTable data={tenants} columns={columns} selectable={true} selectedRows={selectedRows} onSelectionChange={setSelectedRows}
              actions={renderActions} pagination={{ currentPage: pagination.page, totalPages: pagination.totalPages, total: pagination.total, onPageChange: pagination.goToPage }}
              emptyMessage={tp.tenants.noTenants}
            />
          )}
        </div>
      </div>
      {user?.role === 'admin' && (
        <AddTenantModal open={showAddModal} onClose={() => setShowAddModal(false)} onSuccess={fetchTenants} />
      )}
    </PageContainer>
  );
}
